import { useState } from 'react';
import type { FetchUserRoleAssignmentsResponse, UserRoleAssignment } from 'vopay';
import { api } from '@/services/api.config';

/**
 * Direct Pattern hook for fetching the Account-level Roles assigned to a
 * specific user. Hits GET /account/user-roles/user?Username=<email>.
 *
 * Accepts an optional `bearer` so the same drawer can be reused under a
 * sub-account auth context (e.g. from SubAccounts → User Management) without
 * leaking the parent-account JWT into the request.
 *
 * Distinct from `useFetchUserRoles` (Transaction Approval workflow roles)
 * and `useFetchAccountUserRoles` (the full list of Roles on the account).
 */
export default function useFetchUserRoleAssignments(bearer?: string) {
  const [fetching, setFetching] = useState(false);
  const [roles, setRoles] = useState<UserRoleAssignment[]>([]);

  async function fetchUserRoleAssignments(username: string): Promise<FetchUserRoleAssignmentsResponse> {
    // Clear stale state from the previous fetch so a failure here can't leave
    // the previous user's roles visible to the next caller.
    setRoles([]);

    try {
      setFetching(true);
      const res = await api.fetchUserRoleAssignments({ Username: username }, bearer);

      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      }

      setRoles(res.Roles);

      return res;
    } catch (error) {
      console.error('Fetch user role assignments failed:', error);
      throw error;
    } finally {
      setFetching(false);
    }
  }

  function reset() {
    setRoles([]);
  }

  return { fetching, roles, fetchUserRoleAssignments, reset };
}
